import { useState, useEffect, type Dispatch, type SetStateAction } from "react";

import { useAppSelector } from "../../hooks/reduxHooks";
import { selectCurrentProjectId } from "../../ProjectSlice_Store";
import { selectInvalidationCounter } from "../../DocumentPreviewSlice_Store";
import { clearDraftCache } from "../../services/draftEditingService";
import {
  getDocumentPreview,
  getDocumentDownloadSAS,
  getDocumentPreviewWithProgress,
  canPreviewDocument,
  getDocumentPreviewType,
  type DownloadProgressCallback,
} from "../../services/documentPreviewService";
import {
  type DocumentData,
  type PreviewData,
  type ErrorData,
  type DownloadProgress,
  NO_DOWNLOAD_PROGRESS,
  sharePointError,
  usernameFromEmail,
} from "./documentPreviewTypes";

/** Document types whose blob is read into text before rendering. */
const TEXT_PREVIEW_TYPES = new Set([
  "markdown",
  "text",
  "yaml",
  "draft_metadata",
  "draft_content",
  "draft_outline",
]);

interface PreviewSetters {
  setPreviewData: Dispatch<SetStateAction<PreviewData | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<ErrorData | null>>;
  setContentText: Dispatch<SetStateAction<string>>;
  setObjectUrl: Dispatch<SetStateAction<string | null>>;
  setDownloadOnly: Dispatch<SetStateAction<boolean>>;
  setDownloadProgress: Dispatch<SetStateAction<DownloadProgress>>;
}

interface PreviewState extends PreviewSetters {
  previewData: PreviewData | null;
  loading: boolean;
  error: ErrorData | null;
  contentText: string;
  objectUrl: string | null;
  downloadOnly: boolean;
  downloadProgress: DownloadProgress;
}

export interface DocumentPreviewLoad extends PreviewState {
  documentType: string;
  canPreview: boolean;
  /** Re-fetches the document, discarding whatever is currently shown. */
  reload: () => Promise<void>;
}

interface LoadContext {
  document: DocumentData | null;
  projectId: string | null;
  username: string;
  documentType: string;
}

function usePreviewState(): PreviewState {
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorData | null>(null);
  const [contentText, setContentText] = useState<string>("");
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [downloadOnly, setDownloadOnly] = useState<boolean>(false);
  const [downloadProgress, setDownloadProgress] =
    useState<DownloadProgress>(NO_DOWNLOAD_PROGRESS);

  return {
    previewData,
    loading,
    error,
    contentText,
    objectUrl,
    downloadOnly,
    downloadProgress,
    setPreviewData,
    setLoading,
    setError,
    setContentText,
    setObjectUrl,
    setDownloadOnly,
    setDownloadProgress,
  };
}

/**
 * PDFs avoid a full download where possible: blob storage is streamed straight
 * from its SAS URL, SharePoint is downloaded with progress reporting.
 *
 * Returns false when neither path applies and the caller must download in full.
 */
async function fetchPdfDirect(ctx: LoadContext, setters: PreviewSetters): Promise<boolean> {
  const sasData = await getDocumentDownloadSAS(ctx.document, ctx.projectId, ctx.username);

  // A SAS URL lets PDF.js fetch pages on demand rather than downloading upfront
  if (sasData.sasUrl && !sasData.isSharePoint) {
    setters.setPreviewData({ contentBlob: new Blob(), sasData });
    setters.setObjectUrl(sasData.sasUrl);
    return true;
  }

  if (!sasData.isSharePoint) return false;

  // Start at 0% - will update as data comes in
  setters.setDownloadProgress({ isDownloading: true, loaded: 0, total: null, percentage: 0 });
  const onProgress: DownloadProgressCallback = (progress) => {
    setters.setDownloadProgress({
      isDownloading: true,
      loaded: progress.loaded,
      total: progress.total,
      // Null percentage (no content-length) renders as indeterminate progress
      percentage: progress.percentage,
    });
  };

  const preview = await getDocumentPreviewWithProgress(
    ctx.document,
    ctx.projectId,
    ctx.username,
    onProgress
  );
  setters.setDownloadProgress(NO_DOWNLOAD_PROGRESS);
  setters.setPreviewData(preview);
  setters.setObjectUrl(URL.createObjectURL(preview.contentBlob));
  return true;
}

async function fetchFullContent(ctx: LoadContext, setters: PreviewSetters): Promise<void> {
  const preview = await getDocumentPreview(ctx.document, ctx.projectId, ctx.username);
  setters.setPreviewData(preview);
  setters.setObjectUrl(URL.createObjectURL(preview.contentBlob));

  if (TEXT_PREVIEW_TYPES.has(ctx.documentType)) {
    setters.setContentText(await preview.contentBlob.text());
  }
}

function applyLoadError(err: unknown, setters: PreviewSetters): void {
  setters.setDownloadProgress(NO_DOWNLOAD_PROGRESS);

  const mapped = sharePointError(err);
  if (mapped) {
    setters.setError(mapped);
    return;
  }

  const error = err as { code?: string; message?: string };
  if (error.code === "CONVERSION_NOT_SUPPORTED") {
    setters.setDownloadOnly(true);
    return;
  }

  setters.setError({ message: error.message || "Unknown error", type: "generic" });
}

async function loadPreview(ctx: LoadContext, setters: PreviewSetters): Promise<void> {
  setters.setLoading(true);
  setters.setError(null);
  setters.setDownloadOnly(false);
  setters.setPreviewData(null);
  setters.setContentText("");

  try {
    if (ctx.documentType === "pdf" && (await fetchPdfDirect(ctx, setters))) return;
    await fetchFullContent(ctx, setters);
  } catch (err: unknown) {
    applyLoadError(err, setters);
  } finally {
    setters.setLoading(false);
    setters.setDownloadProgress(NO_DOWNLOAD_PROGRESS);
  }
}

/** Loads the previewable content for a document and keeps it in step with the store. */
export function useDocumentPreview(document: DocumentData | null): DocumentPreviewLoad {
  const state = usePreviewState();
  const [loadedDocId, setLoadedDocId] = useState<string | null>(null);

  const projectId = useAppSelector(selectCurrentProjectId);
  const currentUser = useAppSelector((store) => store.userAuthAndAdminSlice);
  const docId = document?.docId;
  // Watch for document invalidation (e.g., when outline/content is regenerated)
  const invalidationCount = useAppSelector(docId ? selectInvalidationCounter(docId) : () => 0);

  const documentType = getDocumentPreviewType(document);
  const canPreview = canPreviewDocument(document);
  const ready = Boolean(document && canPreview && projectId && currentUser?.userEmail);

  const reload = async (): Promise<void> => {
    if (state.objectUrl) {
      URL.revokeObjectURL(state.objectUrl);
      state.setObjectUrl(null);
    }
    const username = usernameFromEmail(currentUser?.userEmail);
    await loadPreview({ document, projectId, username, documentType }, state);
  };

  // Only load when the document being shown actually changes
  useEffect(() => {
    if (ready && docId !== loadedDocId) {
      void reload();
      setLoadedDocId(docId);
    }
  }, [document, projectId, currentUser, loadedDocId]);

  // Reload when the invalidation counter changes (e.g., after generate_outline)
  useEffect(() => {
    if (invalidationCount > 0 && ready) {
      // Clear any cached content for doc-drafter documents
      if (document?.documentCategory === "draft" && docId) {
        clearDraftCache(docId);
      }
      void reload();
    }
  }, [invalidationCount]);

  // Release the object URL when it is replaced or the tab unmounts
  useEffect(() => {
    return () => {
      if (state.objectUrl) {
        URL.revokeObjectURL(state.objectUrl);
      }
    };
  }, [state.objectUrl]);

  return { ...state, documentType, canPreview, reload };
}
